import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import ConfirmDialog from "../components/ConfirmDialog";
import useToast from "../hooks/use-toast-admin";
import { Trash, Envelope, EnvelopeOpen, X, Phone, CheckSquare } from "@phosphor-icons/react";

export default function AdminContactsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkErr, setBulkErr] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const { showToast, ToastView } = useToast();

  const load = () =>
    api
      .get("/contact")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const open = async (c) => {
    setSelected(c);
    if (!c.is_read) {
      try {
        await api.patch(`/contact/${c.id}/read`);
        setItems((prev) =>
          prev.map((x) => (x.id === c.id ? { ...x, is_read: true } : x))
        );
      } catch {}
    }
  };

  const askDelete = (c) => setConfirmTarget({ c, busy: false, err: "" });

  const performDelete = async () => {
    if (!confirmTarget?.c) return;
    setConfirmTarget((s) => ({ ...s, busy: true, err: "" }));
    try {
      await api.delete(`/contact/${confirmTarget.c.id}`);
      const removed = confirmTarget.c;
      setConfirmTarget(null);
      if (selected?.id === removed.id) setSelected(null);
      setItems((prev) => prev.filter((x) => x.id !== removed.id));
      showToast("ok", `Deleted enquiry from ${removed.name}.`);
      load();
    } catch (e) {
      setConfirmTarget((s) => ({ ...s, busy: false, err: formatApiError(e) }));
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-ZA", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  // ---- Bulk selection ----
  const toggleOne = (id) =>
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const allVisibleIds = useMemo(() => items.map((c) => c.id), [items]);
  const allChecked =
    allVisibleIds.length > 0 && allVisibleIds.every((id) => picked.has(id));
  const someChecked = picked.size > 0 && !allChecked;
  const toggleAll = () => {
    setPicked((prev) =>
      allChecked ? new Set() : new Set([...prev, ...allVisibleIds])
    );
  };
  const clearPicked = () => setPicked(new Set());

  const bulkMark = async (action) => {
    if (picked.size === 0) return;
    setBulkBusy(true);
    try {
      const ids = [...picked];
      const { data } = await api.post("/contact/bulk", { ids, action });
      const verb = action === "mark_read" ? "Marked as read" : "Marked as unread";
      showToast("ok", `${verb}: ${data.affected} enquir${data.affected === 1 ? "y" : "ies"}.`);
      clearPicked();
      load();
    } catch (e) {
      showToast("err", formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const askBulkDelete = () => {
    setBulkErr("");
    setBulkConfirmOpen(true);
  };
  const performBulkDelete = async () => {
    if (picked.size === 0) return;
    setBulkBusy(true);
    setBulkErr("");
    try {
      const ids = [...picked];
      const { data } = await api.post("/contact/bulk", { ids, action: "delete" });
      setBulkConfirmOpen(false);
      setItems((prev) => prev.filter((x) => !ids.includes(x.id)));
      clearPicked();
      showToast(
        "ok",
        `Deleted ${data.affected} enquir${data.affected === 1 ? "y" : "ies"}.`
      );
      load();
    } catch (e) {
      setBulkErr(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const unread = items.filter((i) => !i.is_read).length;

  return (
    <div className="p-10" data-testid="admin-contacts-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="overline text-[#a8b2cc] mb-3">Customer Enquiries</div>
          <h1 className="font-display text-5xl tracking-tighter">Enquiries</h1>
          <p className="mt-3 text-[#a8b2cc]">
            {unread} unread · {items.length} total
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-[#a8b2cc] font-mono-accent">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#2c3854] bg-[#141e32] p-16 text-center">
          <div className="font-display text-3xl mb-3">No enquiries yet</div>
          <p className="text-[#a8b2cc]">Messages from the public contact form will show here.</p>
        </div>
      ) : (
        <div className="border border-[#2c3854] bg-[#141e32]">
          <div className="grid grid-cols-[28px_1fr_2fr_1fr_auto] gap-4 px-6 py-4 border-b border-[#2c3854] overline text-[#a8b2cc] items-center">
            <input
              type="checkbox"
              checked={allChecked}
              ref={(el) => { if (el) el.indeterminate = someChecked; }}
              onChange={toggleAll}
              className="accent-[#af3fa6] w-4 h-4 cursor-pointer"
              data-testid="contact-select-all"
              aria-label="Select all"
            />
            <div>From</div>
            <div>Message</div>
            <div>Received</div>
            <div className="w-24 text-right">Actions</div>
          </div>
          {items.map((c) => (
            <div
              key={c.id}
              className={`grid grid-cols-[28px_1fr_2fr_1fr_auto] gap-4 px-6 py-5 border-b border-[#2c3854] hover:bg-[#070b14] transition-colors cursor-pointer items-center ${
                !c.is_read ? "bg-[#070b14]" : ""
              } ${picked.has(c.id) ? "ring-1 ring-inset ring-[#af3fa6]/40" : ""}`}
              onClick={() => open(c)}
              data-testid={`contact-row-${c.id}`}
            >
              <div onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={picked.has(c.id)}
                  onChange={() => toggleOne(c.id)}
                  className="accent-[#af3fa6] w-4 h-4 cursor-pointer"
                  data-testid={`contact-pick-${c.id}`}
                  aria-label={`Select enquiry from ${c.name}`}
                />
              </div>
              <div className="flex items-start gap-3">
                {c.is_read ? (
                  <EnvelopeOpen size={18} weight="duotone" className="text-[#a8b2cc] mt-1" />
                ) : (
                  <Envelope size={18} weight="fill" className="text-[#af3fa6] mt-1" />
                )}
                <div>
                  <div className="font-display text-lg tracking-tight">{c.name}</div>
                  <div className="text-sm text-[#a8b2cc]">{c.email}</div>
                </div>
              </div>
              <div>
                <div className="font-medium text-sm">{c.subject || "(no subject)"}</div>
                <p className="text-sm text-[#a8b2cc] line-clamp-2 mt-1">{c.message}</p>
              </div>
              <div className="text-sm text-[#a8b2cc] pt-1">{formatDate(c.created_at)}</div>
              <div
                className="flex items-center justify-end gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    askDelete(c);
                  }}
                  className="p-2 border border-[#af3fa6] text-[#af3fa6] hover:bg-[#af3fa6] hover:text-white transition-colors rounded-md"
                  aria-label="Delete enquiry"
                  title="Delete enquiry"
                  data-testid={`contact-delete-${c.id}`}
                >
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/60 z-40 flex items-center justify-center p-6"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-[#141e32] border border-[#f5ecd9] w-full max-w-2xl max-h-[90vh] overflow-auto rounded-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="contact-detail-modal"
          >
            <div className="flex items-start justify-between p-8 border-b border-[#2c3854]">
              <div>
                <div className="overline text-[#a8b2cc] mb-2">Enquiry</div>
                <h2 className="font-display text-3xl tracking-tighter">{selected.name}</h2>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#a8b2cc]">
                  <a href={`mailto:${selected.email}`} className="hover:text-[#f5ecd9]">
                    {selected.email}
                  </a>
                  {selected.phone && (
                    <a
                      href={`tel:${selected.phone}`}
                      className="flex items-center gap-1 hover:text-[#f5ecd9]"
                    >
                      <Phone size={14} weight="bold" /> {selected.phone}
                    </a>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="p-2 hover:bg-[#070b14] rounded-md"
                data-testid="contact-detail-close"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-8">
              {selected.subject && (
                <div className="mb-5">
                  <div className="overline text-[#a8b2cc] mb-1">Subject</div>
                  <div className="font-display text-xl">{selected.subject}</div>
                </div>
              )}
              <div>
                <div className="overline text-[#a8b2cc] mb-2">Message</div>
                <p className="whitespace-pre-line leading-relaxed text-[#f5ecd9]">
                  {selected.message}
                </p>
              </div>
              <div className="mt-8 text-sm text-[#a8b2cc]">
                Received {formatDate(selected.created_at)}
              </div>
            </div>
            <div className="p-6 border-t border-[#2c3854] flex flex-wrap justify-between gap-3">
              <button
                onClick={() => askDelete(selected)}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#af3fa6] text-[#af3fa6] hover:bg-[#af3fa6] hover:text-white transition-colors font-nav text-xs uppercase tracking-[0.18em] font-semibold"
                data-testid="contact-detail-delete"
              >
                <Trash size={14} weight="bold" /> Delete enquiry
              </button>
              <a
                href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || "Your enquiry")}`}
                className="btn-primary"
                data-testid="contact-reply-button"
              >
                <Envelope size={16} weight="bold" /> Reply via Email
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Floating bulk-action toolbar */}
      {picked.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#141e32] border border-[#af3fa6] rounded-full shadow-2xl flex items-center gap-2 pl-5 pr-2 py-2"
          data-testid="bulk-toolbar"
        >
          <CheckSquare size={18} weight="fill" className="text-[#af3fa6]" />
          <span className="font-display text-sm tracking-tight text-[#f5ecd9]" data-testid="bulk-count">
            {picked.size} selected
          </span>
          <span className="mx-2 text-[#2c3854]">|</span>
          <button
            onClick={() => bulkMark("mark_read")}
            disabled={bulkBusy}
            className="px-3 py-1.5 rounded-full text-[0.7rem] uppercase tracking-[0.16em] font-nav font-semibold border border-[#2c3854] text-[#a8b2cc] hover:text-[#f5ecd9] hover:border-[#f5ecd9] disabled:opacity-50"
            data-testid="bulk-mark-read"
          >
            Mark as read
          </button>
          <button
            onClick={() => bulkMark("mark_unread")}
            disabled={bulkBusy}
            className="px-3 py-1.5 rounded-full text-[0.7rem] uppercase tracking-[0.16em] font-nav font-semibold border border-[#2c3854] text-[#a8b2cc] hover:text-[#f5ecd9] hover:border-[#f5ecd9] disabled:opacity-50"
            data-testid="bulk-mark-unread"
          >
            Mark unread
          </button>
          <button
            onClick={askBulkDelete}
            disabled={bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[0.7rem] uppercase tracking-[0.16em] font-nav font-semibold bg-[#af3fa6] text-white hover:bg-[#8a2f83] disabled:opacity-50"
            data-testid="bulk-delete"
          >
            <Trash size={12} weight="bold" /> Delete
          </button>
          <button
            onClick={clearPicked}
            disabled={bulkBusy}
            className="ml-1 p-1.5 rounded-full hover:bg-[#070b14] text-[#a8b2cc] disabled:opacity-50"
            data-testid="bulk-clear"
            aria-label="Clear selection"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      )}

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${picked.size} enquir${picked.size === 1 ? "y" : "ies"}?`}
        description={
          <>
            Permanently removes <span className="text-[#f5ecd9]">{picked.size}</span> selected
            enquir{picked.size === 1 ? "y" : "ies"}. This cannot be undone.
          </>
        }
        confirmLabel={`Yes, delete ${picked.size}`}
        onCancel={() => setBulkConfirmOpen(false)}
        onConfirm={performBulkDelete}
        busy={bulkBusy}
        error={bulkErr}
        testId="bulk-confirm-delete"
      />

      <ConfirmDialog
        open={!!confirmTarget}
        title="Delete this enquiry?"
        description={
          confirmTarget?.c ? (
            <>
              Permanently removes the enquiry from{" "}
              <span className="text-[#f5ecd9]">{confirmTarget.c.name}</span> (
              {confirmTarget.c.email}). This cannot be undone.
            </>
          ) : null
        }
        confirmLabel="Yes, delete"
        onCancel={() => setConfirmTarget(null)}
        onConfirm={performDelete}
        busy={confirmTarget?.busy}
        error={confirmTarget?.err}
        testId="contact-confirm-delete"
      />
      <ToastView />
    </div>
  );
}
